import React from "react";
import { useNavigate } from "react-router-dom";
import NeoButton from "../../../../components/Common/NeoButton";
import NeoBadge from "../../../../components/Common/NeoBadge";
import PlatformClientHealthBadge from "./PlatformClientHealthBadge";
import { BILLING_STATUS_LABELS } from "../constants";

const PlatformClientDetail = ({ client, isManager, onTransfer }) => {
    const navigate = useNavigate();

    if (!client) {
        return (
            <div className="d-flex align-items-center justify-content-center h-100 py-5 text-muted">
                <div className="text-center">
                    <i className="mdi mdi-briefcase-outline font-size-36 d-block mb-2 opacity-25" />
                    <span className="font-size-13 fw-semibold">Selecione um cliente para ver os detalhes.</span>
                </div>
            </div>
        );
    }

    const billing = BILLING_STATUS_LABELS[client.billingStatus] || { label: client.billingStatus || "—", color: "muted" };
    const ob = client.onboardingProgress;
    const health = client.health;

    return (
        <div className="p-4 d-flex flex-column gap-4">
            {/* Header do cliente */}
            <div className="d-flex align-items-start justify-content-between gap-2">
                <div>
                    <h5 className="fw-bold text-dark mb-1">{client.name}</h5>
                    <div className="d-flex gap-2 flex-wrap">
                        <NeoBadge color={billing.color}>{billing.label}</NeoBadge>
                        {client.planId && (
                            <NeoBadge color="muted">{client.planId}</NeoBadge>
                        )}
                    </div>
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                    <NeoButton variant="soft-primary" size="sm" onClick={() => navigate(`/platform/tenants/${client.id}`)}>
                        <i className="mdi mdi-office-building-outline me-1" />
                        Ver Cadastro
                    </NeoButton>
                    {isManager && (
                        <NeoButton variant="soft-secondary" size="sm" onClick={() => onTransfer(client)}>
                            <i className="mdi mdi-swap-horizontal me-1" />
                            Transferir
                        </NeoButton>
                    )}
                </div>
            </div>

            {/* Health Score */}
            {health && (
                <div className="csm-info-block">
                    <small className="text-muted d-block font-size-10 text-uppercase fw-bold mb-2">Health Score</small>
                    <div className="d-flex align-items-center gap-3 mb-3">
                        <PlatformClientHealthBadge score={health.score} level={health.level} />
                    </div>
                    <div className="d-flex gap-3 flex-wrap">
                        {[
                            { label: "Onboarding", value: health.breakdown?.onboarding, weight: "30%" },
                            { label: "Uso", value: health.breakdown?.usage, weight: "30%" },
                            { label: "Financeiro", value: health.breakdown?.billing, weight: "25%" },
                            { label: "Engaj. CS", value: health.breakdown?.csmEngagement, weight: "15%" },
                        ].map(d => (
                            <div key={d.label} className="csm-grid-item">
                                <div className="fw-bold font-size-13 text-dark">{d.value ?? "—"}</div>
                                <div className="text-muted font-size-10">{d.label}</div>
                                <div className="text-muted font-size-9 opacity-75">{d.weight}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Onboarding */}
            {ob && (
                <div>
                    <small className="text-muted d-block font-size-10 text-uppercase fw-bold mb-2">
                        Onboarding
                    </small>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="font-size-12 text-dark">{ob.checked} de {ob.total} etapas concluídas</span>
                        <span className="fw-bold font-size-12">{ob.pct}%</span>
                    </div>
                    <div className="progress csm-progress-container-lg">
                        <div
                            className={`progress-bar ${ob.pct === 100 ? "csm-progress-bar-success" : "csm-progress-bar-brand"}`}
                            style={{ width: `${ob.pct}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Responsáveis */}
            <div>
                <small className="text-muted d-block font-size-10 text-uppercase fw-bold mb-2">Responsáveis</small>
                <div className="d-flex flex-column gap-2">
                    <div className="csm-info-block">
                        <small className="text-muted font-size-10 fw-bold">CS Responsável</small>
                        <div className="fw-semibold font-size-13 text-dark">{client.csmName || "Não atribuído"}</div>
                        {client.csmAssignedAt && (
                            <small className="text-muted font-size-10">
                                Desde {new Date(client.csmAssignedAt).toLocaleDateString("pt-BR")}
                            </small>
                        )}
                    </div>
                    {client.salesConsultantName && (
                        <div className="csm-info-block">
                            <small className="text-muted font-size-10 fw-bold">Consultor de Vendas</small>
                            <div className="fw-semibold font-size-13 text-dark">{client.salesConsultantName}</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Último contato */}
            {client.daysSinceContact !== null && client.daysSinceContact !== undefined && (
                <div className={client.daysSinceContact > 14 ? "csm-info-block-danger" : "csm-info-block"}>
                    <small className="text-muted font-size-10 text-uppercase fw-bold d-block mb-1">Último Contato</small>
                    <span className={`fw-bold font-size-14 ${client.daysSinceContact > 14 ? "text-danger" : "text-dark"}`}>
                        {client.daysSinceContact === 0 ? "Hoje" : `Há ${client.daysSinceContact} dias`}
                    </span>
                    {client.daysSinceContact > 14 && (
                        <small className="d-block text-danger font-size-11 mt-1">
                            <i className="mdi mdi-alert-outline me-1" />
                            Cliente sem contato há mais de 2 semanas.
                        </small>
                    )}
                </div>
            )}

            {/* Criado em */}
            {client.createdAt && (
                <div className="text-muted font-size-11">
                    <i className="mdi mdi-calendar-outline me-1" />
                    Cliente desde {new Date(client.createdAt).toLocaleDateString("pt-BR")}
                </div>
            )}
        </div>
    );
};

export default PlatformClientDetail;
